import { OperationOutcomeError, isNotFound } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { CodeSystem, HealthcareService } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import { useEffect, useState, useSyncExternalStore } from 'react';
import {
  CALENDAR_EVENT_CATEGORY_SYSTEM,
  PLATFORM_HEALTHCARE_SERVICE_IDENTIFIER_SYSTEM,
  PLATFORM_HEALTHCARE_SERVICE_IDENTIFIER_VALUE,
  THERAPY_SERVICE_TYPE_SYSTEM,
} from '@unify/fhir-model/constants';
import { CODE_SYSTEM_IDS } from '@unify/fhir-model/structural-ids';
import { setEpisodeTypeCatalogLabels } from '../../lib/episodeTypeDisplayRegistry';
import {
  getEocTypeApprovedSessionIds,
  getEocTypeIntakeType,
  getEocTypeSessionMap,
  getEocTypeSoharSpecialtyCode,
  getHealthcareServiceTypeCode,
  hasRequiredEocSessions,
  isHealthcareServiceActive,
  markHealthcareServiceAsPlatformCatalog,
  setEocTypeExtensions,
  type EocIntakeType,
  type EocSessionMap,
} from '@unify/fhir-model/resources/healthcare-service';
import type { AppointmentSessionType } from './appointmentTypeCatalog';

export interface EpisodeOfCareType {
  id: string;
  name: string;
  code: string;
  active: boolean;
  intakeType: EocIntakeType;
  intakeSessionTypeId: string | null;
  consultationSessionTypeId: string | null;
  standardSessionTypeId: string | null;
  soharSpecialtyCode: string;
  /** Additional approved clinical-session ids beyond the implicitly-approved session map. */
  approvedSessionTypeIds: string[];
}

export const EOC_TYPE_SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

export function slugifyEocTypeName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** True when every session type applicable to the intake type is set (required to activate). */
export function canActivateEpisodeOfCareType(
  intakeType: EocIntakeType,
  sessionMap: EocSessionMap,
): boolean {
  return hasRequiredEocSessions(intakeType, sessionMap);
}

export function hsToEocType(hs: HealthcareService): EpisodeOfCareType {
  const sessionMap = getEocTypeSessionMap(hs);
  return {
    id: hs.id ?? '',
    name: hs.name ?? '',
    code: getHealthcareServiceTypeCode(hs) ?? '',
    active: isHealthcareServiceActive(hs),
    intakeType: getEocTypeIntakeType(hs),
    intakeSessionTypeId: sessionMap.intake,
    consultationSessionTypeId: sessionMap.consultation,
    standardSessionTypeId: sessionMap.standard,
    soharSpecialtyCode: getEocTypeSoharSpecialtyCode(hs) ?? '',
    approvedSessionTypeIds: getEocTypeApprovedSessionIds(hs),
  };
}

/**
 * Effective approved clinical-session ids for an episode type: the
 * intake/consultation/standard mappings (implicitly approved) plus the
 * explicit approved extras.
 */
export function effectiveApprovedSessionTypeIds(type: EpisodeOfCareType): Set<string> {
  return new Set(
    [
      type.intakeSessionTypeId,
      type.consultationSessionTypeId,
      type.standardSessionTypeId,
      ...type.approvedSessionTypeIds,
    ].filter((id): id is string => Boolean(id)),
  );
}

/**
 * Restrict a provider's schedulable session types to an episode type's
 * effective approved set. Fails open (returns `providerTypes` unchanged) when
 * the episode type is unknown or has no configured sessions; never
 * re-introduces inactive/intake-only entries because `providerTypes` are
 * already schedulable-only.
 */
export function filterSessionTypesForEpisodeType(
  providerTypes: AppointmentSessionType[],
  eocType: EpisodeOfCareType | undefined,
): AppointmentSessionType[] {
  if (!eocType) return providerTypes;
  const approved = effectiveApprovedSessionTypeIds(eocType);
  if (approved.size === 0) return providerTypes;
  return providerTypes.filter((t) => approved.has(t.id));
}

// ---------------------------------------------------------------------------
// Catalog store
// ---------------------------------------------------------------------------

let catalogVersion = 0;
const catalogListeners = new Set<() => void>();

function getCatalogVersion(): number {
  return catalogVersion;
}

function subscribeCatalog(listener: () => void): () => void {
  catalogListeners.add(listener);
  return () => catalogListeners.delete(listener);
}

export function emitCatalogChange(): void {
  catalogVersion += 1;
  catalogListeners.forEach((listener) => listener());
}

function useCatalogVersion(): number {
  return useSyncExternalStore(subscribeCatalog, getCatalogVersion, getCatalogVersion);
}

export function buildEocTypeSearch(): Record<string, string> {
  return {
    'service-category': `${CALENDAR_EVENT_CATEGORY_SYSTEM}|episode-of-care`,
    identifier: `${PLATFORM_HEALTHCARE_SERVICE_IDENTIFIER_SYSTEM}|${PLATFORM_HEALTHCARE_SERVICE_IDENTIFIER_VALUE}`,
    _count: '100',
  };
}

/** Load admin EoC type names into the shared display registry (caseload, chart, etc.). */
export async function syncEpisodeTypeCatalogLabels(medplum: MedplumClient): Promise<void> {
  const resources = await medplum.searchResources('HealthcareService', buildEocTypeSearch());
  const mapped = resources.map(hsToEocType).filter((row) => row.id);
  setEpisodeTypeCatalogLabels(
    Object.fromEntries(mapped.filter((t) => t.code).map((t) => [t.code, t.name])),
  );
}

export function useEpisodeOfCareTypes(): { types: EpisodeOfCareType[]; loading: boolean } {
  const medplum = useMedplum();
  const version = useCatalogVersion();
  const [types, setTypes] = useState<EpisodeOfCareType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    medplum.invalidateSearches('HealthcareService');
    medplum
      .searchResources('HealthcareService', buildEocTypeSearch())
      .then((resources) => {
        if (cancelled) return;
        const mapped = resources.map(hsToEocType).filter((row) => row.id);
        setEpisodeTypeCatalogLabels(
          Object.fromEntries(mapped.filter((t) => t.code).map((t) => [t.code, t.name])),
        );
        setTypes(mapped);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[catalog] failed to load episode-of-care types', err);
          setTypes([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, version]);

  return { types, loading };
}

// ---------------------------------------------------------------------------
// Catalog writes (Unify Admin only: the `unifyAdmin` AccessPolicy is the only one with
// HealthcareService / CodeSystem write access)
// ---------------------------------------------------------------------------

export interface UpsertEpisodeOfCareTypeInput {
  id?: string;
  name: string;
  code: string;
  active: boolean;
  intakeType: EocIntakeType;
  sessionMap: EocSessionMap;
  soharSpecialtyCode: string;
  /** Extras beyond the implicitly approved session map entries (UI field: `approvedSessionTypeIds`). */
  approvedSessionIds: string[];
}

async function syncTherapyServiceTypeConcept(
  medplum: MedplumClient,
  code: string,
  display: string,
): Promise<void> {
  const id = CODE_SYSTEM_IDS.therapyServiceType;
  let cs: CodeSystem;
  try {
    cs = await medplum.readResource('CodeSystem', id);
  } catch (err) {
    if (!(err instanceof OperationOutcomeError) || !isNotFound(err.outcome)) throw err;
    await medplum.createResource({
      resourceType: 'CodeSystem',
      id,
      url: THERAPY_SERVICE_TYPE_SYSTEM,
      status: 'active',
      content: 'fragment',
      concept: [{ code, display }],
    });
    return;
  }
  const concepts = [...(cs.concept ?? [])];
  const idx = concepts.findIndex((c) => c.code === code);
  if (idx >= 0) concepts[idx] = { ...concepts[idx], code, display };
  else concepts.push({ code, display });
  await medplum.updateResource({ ...cs, concept: concepts });
}

function buildEocHealthcareService(
  input: UpsertEpisodeOfCareTypeInput,
  code: string,
  existing?: HealthcareService,
): HealthcareService {
  const name = input.name.trim();
  let hs: HealthcareService = {
    resourceType: 'HealthcareService',
    id: existing?.id,
    active: input.active,
    name,
    category: [
      {
        coding: [
          { system: CALENDAR_EVENT_CATEGORY_SYSTEM, code: 'episode-of-care', display: 'Episode of Care Type' },
        ],
      },
    ],
    type: [{ coding: [{ system: THERAPY_SERVICE_TYPE_SYSTEM, code, display: name }] }],
    appointmentRequired: false,
    extension: existing?.extension ? [...existing.extension] : [],
  };
  hs = markHealthcareServiceAsPlatformCatalog(hs);
  hs.extension = setEocTypeExtensions(
    hs,
    input.intakeType,
    input.sessionMap,
    input.soharSpecialtyCode,
    input.approvedSessionIds,
  );
  if (existing?.meta) hs.meta = existing.meta;
  return hs;
}

/**
 * Create or edit an episode-of-care type: sync the `therapy-service-type` CodeSystem concept first,
 * then write the HealthcareService. The code is fixed after create, and new types are created inactive.
 */
export async function upsertEpisodeOfCareType(
  medplum: MedplumClient,
  input: UpsertEpisodeOfCareTypeInput,
): Promise<EpisodeOfCareType> {
  let code = input.code.trim();
  let existing: HealthcareService | undefined;
  if (input.id) {
    existing = await medplum.readResource('HealthcareService', input.id);
    code = getHealthcareServiceTypeCode(existing) ?? code;
  } else {
    const all = await medplum.searchResources('HealthcareService', buildEocTypeSearch());
    if (all.some((hs) => getHealthcareServiceTypeCode(hs) === code)) {
      throw new Error(`Episode of care type with code "${code}" already exists`);
    }
  }

  await syncTherapyServiceTypeConcept(medplum, code, input.name.trim());

  const hs = buildEocHealthcareService(input, code, existing);
  const saved = existing?.id
    ? await medplum.updateResource({ ...existing, ...hs, id: existing.id })
    : await medplum.createResource({ ...hs, id: undefined, active: false });
  medplum.invalidateSearches('HealthcareService');
  emitCatalogChange();
  return hsToEocType(saved);
}
